/**
 * Subscription Management API Contract (plan #004, U6 — single write path).
 *
 * `manage-subscription` is the sole write path to the single `Subscription`
 * table (KTD-10), replacing the old delete-all+create-one single-plan writer.
 * It assigns a customer to a subscription, flips lifecycle status deliberately,
 * attaches/detaches add-ons, and removes a subscription. Super-admin gated.
 *
 *  - GET  /v1/schools/:school_id/subscriptions          → list a school's subscriptions
 *  - POST /v1/schools/:school_id/manage-subscription     → one of the actions below
 *
 * Restricted-read (R5): the billing linkage fields are stripped server-side
 * and present ONLY on the super-admin response variant. The contract expresses
 * this with two response variants — `Subscription` (base) vs `SubscriptionAdmin`
 * (with billing ref). See `schemas/entities/subscription.ts`.
 */

import { z } from 'zod'
import {
  SubscriptionSchema,
  SubscriptionAdminSchema,
  SubscriptionStatusSchema
} from '../../schemas/entities/subscription'

// ----- GET a school's subscriptions -----

export const ListSubscriptionsRequestSchema = z.object({
  school_public_id: z.string().describe('School unique identifier')
})
export type ListSubscriptionsRequest = z.infer<typeof ListSubscriptionsRequestSchema>

/**
 * Base list response (school-admin/member): no billing ref (R5).
 */
export const ListSubscriptionsResponseSchema = z.object({
  data: z.array(SubscriptionSchema),
  message: z.string()
})
export type ListSubscriptionsResponse = z.infer<typeof ListSubscriptionsResponseSchema>

/**
 * Super-admin list response: carries the restricted billing linkage (R5).
 */
export const ListSubscriptionsAdminResponseSchema = z.object({
  data: z.array(SubscriptionAdminSchema),
  message: z.string()
})
export type ListSubscriptionsAdminResponse = z.infer<typeof ListSubscriptionsAdminResponseSchema>

// ----- POST manage-subscription (discriminated by action) -----

/**
 * Assign a customer to a subscription (or upsert the (owner, product) row).
 * `planId` is the baseline plan. `status` defaults to `trial` (never auto
 * `paying` — KTD-7). Billing linkage is super-admin-write only and is never
 * echoed back to a non-super-admin (R5).
 */
export const AssignSubscriptionActionSchema = z.object({
  action: z.literal('assign'),
  productId: z.string().describe('Product public ID'),
  planId: z.string().describe('Plan public ID (baseline grant set)'),
  status: SubscriptionStatusSchema.optional().describe('Initial lifecycle status (defaults to trial)'),
  billingProvider: z.string().nullable().optional().describe('Billing provider (super-admin write only)'),
  billingExternalRef: z.string().nullable().optional().describe('External billing reference (super-admin write only)')
})

/**
 * Deliberately flip an existing subscription's lifecycle status (KTD-4/KTD-7).
 */
export const FlipStatusActionSchema = z.object({
  action: z.literal('flip-status'),
  subscriptionId: z.string().describe('Subscription public ID'),
  status: SubscriptionStatusSchema.describe('New lifecycle status')
})

/**
 * Attach an add-on to a subscription (creates a `SubscriptionAddOn`).
 */
export const AttachAddOnActionSchema = z.object({
  action: z.literal('attach-add-on'),
  subscriptionId: z.string().describe('Subscription public ID'),
  addOnId: z.string().describe('Add-on public ID to attach')
})

/**
 * Detach an add-on from a subscription.
 */
export const DetachAddOnActionSchema = z.object({
  action: z.literal('detach-add-on'),
  subscriptionId: z.string().describe('Subscription public ID'),
  addOnId: z.string().describe('Add-on public ID to detach')
})

/**
 * Remove a subscription (marked churned/removed per policy; siblings untouched).
 */
export const RemoveSubscriptionActionSchema = z.object({
  action: z.literal('remove'),
  subscriptionId: z.string().describe('Subscription public ID')
})

export const ManageSubscriptionRequestSchema = z.discriminatedUnion('action', [
  AssignSubscriptionActionSchema,
  FlipStatusActionSchema,
  AttachAddOnActionSchema,
  DetachAddOnActionSchema,
  RemoveSubscriptionActionSchema
])
export type ManageSubscriptionRequest = z.infer<typeof ManageSubscriptionRequestSchema>

/**
 * Base mutation response (school-admin/member): no billing ref (R5).
 */
export const ManageSubscriptionResponseSchema = z.object({
  data: SubscriptionSchema,
  message: z.string()
})
export type ManageSubscriptionResponse = z.infer<typeof ManageSubscriptionResponseSchema>

/**
 * Super-admin mutation response: carries the restricted billing linkage (R5).
 */
export const ManageSubscriptionAdminResponseSchema = z.object({
  data: SubscriptionAdminSchema,
  message: z.string()
})
export type ManageSubscriptionAdminResponse = z.infer<typeof ManageSubscriptionAdminResponseSchema>
